import React from "react";
import { formatDecimal } from "@globals/format";
import { useMetricMeta } from "@client/hooks/useMetricMeta";
import { t } from "@client/lib/dictionary";
import type { TracerStatistics } from "@client/types/tracer";
import { formatMetric, metricLabel } from "@client/utils/formatMetric";
import { Card, CardContent, CardHeader, CardTitle } from "@client/components/ui/card";

interface Props {
  slug: string;
  statistics: TracerStatistics | null | undefined;
}

const fmt = (n: number, digits: number = 2) => formatDecimal(Number(n), digits);

const Cell: React.FC<{ label: string; value: string; unit?: string; tone?: "generated" | "consumed" }> = ({
  label,
  value,
  unit,
  tone,
}) => {
  const color = tone === "generated" ? "text-green-600" : tone === "consumed" ? "text-blue-500" : "text-foreground";
  return (
    <div className="flex flex-col p-3 rounded border border-border/50">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-lg font-bold tabular-nums ${color}`}>
        {value}
        {unit && <span className="text-sm font-normal ml-1 text-muted-foreground">{unit}</span>}
      </span>
    </div>
  );
};

export const TracerStatsCards: React.FC<Props> = ({ slug, statistics }) => {
  const topic = (metric: string) => `zige/tracer-${slug}/${metric}/val`;
  // MQTT-published metrics — label/unit/decimals z DB
  const genTodayMeta = useMetricMeta(topic("energy-gen-today-kwh"));
  const genTotalMeta = useMetricMeta(topic("energy-gen-total-kwh"));

  if (!statistics) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t.tracer.detail.stats}</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground text-sm">Čekám na statistiky…</CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.tracer.detail.stats}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Cell
            label={metricLabel(genTodayMeta, t.tracer.stats.energyGenToday)}
            value={formatMetric(statistics.energyGeneratedTodayKwh, genTodayMeta)}
            tone="generated"
          />
          {/* Month/year nemají MQTT topic — hardcoded label + fmt */}
          <Cell
            label={t.tracer.stats.energyGenMonth}
            value={fmt(statistics.energyGeneratedMonthKwh)}
            unit="kWh"
            tone="generated"
          />
          <Cell
            label={t.tracer.stats.energyGenYear}
            value={fmt(statistics.energyGeneratedYearKwh)}
            unit="kWh"
            tone="generated"
          />
          <Cell
            label={metricLabel(genTotalMeta, t.tracer.stats.energyGenTotal)}
            value={formatMetric(statistics.energyGeneratedTotalKwh, genTotalMeta)}
            tone="generated"
          />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Cell label={t.tracer.stats.pvVoltageMaxToday} value={fmt(statistics.pvVoltageMaxTodayV)} unit="V" />
          <Cell label={t.tracer.stats.pvVoltageMinToday} value={fmt(statistics.pvVoltageMinTodayV)} unit="V" />
          <Cell
            label={t.tracer.stats.batteryVoltageMaxToday}
            value={fmt(statistics.batteryVoltageMaxTodayV)}
            unit="V"
          />
          <Cell
            label={t.tracer.stats.batteryVoltageMinToday}
            value={fmt(statistics.batteryVoltageMinTodayV)}
            unit="V"
          />
        </div>
        <Cell label={t.tracer.stats.co2Reduction} value={fmt(statistics.co2ReductionTonnes, 3)} unit="t" />
      </CardContent>
    </Card>
  );
};
